import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { devtools } from 'zustand/middleware';

/**
 * Community Store
 * Manages community selection and preferences
 */
const useCommunityStore = create(
  devtools(
    persist(
      (set, get) => ({
        // ========== STATE ==========
        selectedCommunity: null,
        recentCommunities: [],
        preferences: {
          defaultView: 'grid', // 'grid' or 'list'
          sortBy: 'recent', // 'recent', 'popular', 'alphabetical'
        },

        // ========== ACTIONS ==========

        /**
         * Set the currently selected community
         * @param {Object|null} community - Community object or null to clear
         */
        setSelectedCommunity: (community) => {
          set({ selectedCommunity: community }, false, 'community/setSelected');

          // Add to recent communities if not null
          if (community) {
            get().addRecentCommunity(community);
          }
        },

        /**
         * Add a community to recent list
         * @param {Object} community - Community object
         */
        addRecentCommunity: (community) => {
          set(
            (state) => {
              // Remove if already in list
              const filtered = state.recentCommunities.filter(
                (c) => c.id !== community.id && c.oid !== community.oid
              );

              // Add to beginning and limit to 10
              return {
                recentCommunities: [community, ...filtered].slice(0, 10),
              };
            },
            false,
            'community/addRecent'
          );
        },

        /**
         * Clear recent communities list
         */
        clearRecentCommunities: () =>
          set({ recentCommunities: [] }, false, 'community/clearRecent'),

        /**
         * Update community preferences
         * @param {Object} updates - Partial preferences object
         */
        updatePreferences: (updates) =>
          set(
            (state) => ({
              preferences: { ...state.preferences, ...updates },
            }),
            false,
            'community/updatePreferences'
          ),

        /**
         * Get community by ID from recent list
         * @param {string} id - Community ID or OID
         * @returns {Object|null} Community object or null
         */
        getCommunityById: (id) => {
          const state = get();
          return (
            state.recentCommunities.find((c) => c.id === id || c.oid === id) ||
            null
          );
        },

        /**
         * Reset community state
         */
        reset: () =>
          set(
            {
              selectedCommunity: null,
              recentCommunities: [],
              preferences: {
                defaultView: 'grid',
                sortBy: 'recent',
              },
            },
            false,
            'community/reset'
          ),
      }),
      {
        name: 'community-storage',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          selectedCommunity: state.selectedCommunity,
          recentCommunities: state.recentCommunities,
          preferences: state.preferences,
        }),
        version: 1,
      }
    ),
    {
      name: 'CommunityStore',
      enabled: import.meta.env.DEV,
    }
  )
);

// ========== SELECTORS ==========

export const selectSelectedCommunity = (state) => state.selectedCommunity;
export const selectRecentCommunities = (state) => state.recentCommunities;
export const selectCommunityPreferences = (state) => state.preferences;
export const selectDefaultView = (state) => state.preferences.defaultView;
export const selectSortBy = (state) => state.preferences.sortBy;

export default useCommunityStore;
